import * as Haptics from 'expo-haptics';
import { useMemo } from 'react';
import { Platform } from 'react-native';

const isNative = () => Platform.OS !== 'web';

/**
 * Centralized haptic feedback hook providing a semantic, web-safe API over expo-haptics.
 *
 * All methods are no-ops on web. Implemented as a hook (rather than plain utilities)
 * to allow future extension with user haptic preferences from Jotai state without
 * changing call sites.
 *
 * @example
 * const haptic = useHaptic();
 * haptic.selection(); // for toggles, checkboxes, tabs
 * haptic.impactLight(); // for button presses, sheet open
 * haptic.warning(); // for destructive confirmations
 */
export function useHaptic() {
  return useMemo(
    () => ({
      /** Light tap — toggles, switches, checkboxes, radio buttons, tab switches */
      selection: () => {
        if (isNative()) {
          void Haptics.selectionAsync();
        }
      },

      /** Soft thud — button presses, bottom sheet open, pull-to-refresh trigger */
      impactLight: () => {
        if (isNative()) {
          void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
        }
      },

      /** Medium thud — important actions, long-press activations, form submissions */
      impactMedium: () => {
        if (isNative()) {
          void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
        }
      },

      /** Heavy thud — destructive confirmation dialogs */
      impactHeavy: () => {
        if (isNative()) {
          void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
        }
      },

      /** Positive outcome — form submission success, save complete */
      success: () => {
        if (isNative()) {
          void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        }
      },

      /** Negative outcome — validation error, failed action */
      error: () => {
        if (isNative()) {
          void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
        }
      },

      /** Caution — destructive action about to happen */
      warning: () => {
        if (isNative()) {
          void Haptics.notificationAsync(Haptics.NotificationFeedbackType.Warning);
        }
      },
    }),
    []
  );
}
